import React, { createContext, useContext, useState, useEffect } from 'react';

const ImageContext = createContext();

export const useImage = () => {
  const context = useContext(ImageContext);
  if (!context) {
    throw new Error('useImage must be used within an ImageProvider');
  }
  return context;
};

export const ImageProvider = ({ children }) => {
  const [images, setImages] = useState(new Map());
  const [loading, setLoading] = useState(new Set());
  const [errors, setErrors] = useState(new Set());

  const loadImage = async (src) => {
    // If already loaded, return immediately
    if (images.has(src)) {
      return images.get(src);
    }

    // If already loading, wait for it
    if (loading.has(src)) {
      return new Promise((resolve, reject) => {
        const checkLoaded = () => {
          if (images.has(src)) {
            resolve(images.get(src));
          } else if (errors.has(src)) {
            reject(new Error(`Failed to load image: ${src}`));
          } else {
            setTimeout(checkLoaded, 50);
          }
        };
        checkLoaded();
      });
    }

    // Start loading
    setLoading(prev => new Set(prev).add(src));

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const imageData = {
          src: img.src,
          loaded: true,
          element: img
        };
        setImages(prev => new Map(prev).set(src, imageData));
        setLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(src);
          return newSet;
        });
        resolve(imageData);
      };
      img.onerror = () => {
        setErrors(prev => new Set(prev).add(src));
        setLoading(prev => {
          const newSet = new Set(prev);
          newSet.delete(src);
          return newSet;
        });
        reject(new Error(`Failed to load image: ${src}`));
      };
      img.src = src;
    });
  };

  const preloadImages = async (srcs) => {
    const promises = srcs.map(src => loadImage(src));
    return Promise.allSettled(promises);
  };

  const isLoaded = (src) => images.has(src);
  const isLoading = (src) => loading.has(src);
  const hasError = (src) => errors.has(src);

  // DISABLED: No preloading to prevent multiple requests
  // useEffect(() => {
  //   const commonImages = [
  //     '/Category/icon.png',
  //     '/Category/1.png',
  //     '/Category/2.png',
  //     '/Category/3.png',
  //     '/Category/4.png',
  //     '/Category/5.png',
  //     '/Category/6.png'
  //   ];

  //   preloadImages(commonImages)
  //     .then(() => {
  //       console.log('Common images preloaded successfully');
  //     })
  //     .catch((error) => {
  //       console.log('Some images failed to preload:', error);
  //     });
  // }, []);

  const value = {
    images,
    loadImage,
    preloadImages,
    isLoaded,
    isLoading,
    hasError
  };

  return (
    <ImageContext.Provider value={value}>
      {children}
    </ImageContext.Provider>
  );
};
